import React, { FC } from 'react';
import { View } from 'react-native';
import { observer } from 'mobx-react';

import { COLORS } from '../../../config/designSystem/Colors';
import { MemoCard } from './MemoCard';
import { SPACING } from '../../../config/designSystem/Spacing';
import { useDiaryFrame } from '../useDiaryFrame';
import { useDiaryMaxWidth } from '../ui/useDiaryMaxWidth';

interface MemoCardFrameProps {
  /** position of the card among the adjacent memo cards of the session */
  dots?: { index: number; total: number };
  staticFrame: {
    mainTitle?: string;
    /** the card overline ("Challenge") travels as the title's secondary value */
    secondaryTitle?: string;
    text?: { value?: string | null }[];
    emoji?: string;
  };
}

/**
 * figma: one memo card of a memo challenge. A display-only frame: a
 * staticFrame question of type memoCard, one session step per card, so the
 * session's Continue/Finish pages through the cards like any other frames.
 */
const _MemoCardFrame: FC<MemoCardFrameProps> = ({ staticFrame, dots }) => {
  const { moduleGradient } = useDiaryFrame();
  const maxWidth = useDiaryMaxWidth();
  const card = {
    overline: staticFrame.secondaryTitle || undefined,
    title: staticFrame.mainTitle || undefined,
    body: staticFrame.text?.[0]?.value || undefined,
    emoji: staticFrame.emoji || undefined,
  };
  return (
    <View style={{ alignItems: 'center', paddingHorizontal: SPACING['sp-16'] }}>
      <View style={{ width: '100%', maxWidth, rowGap: SPACING['sp-16'], alignItems: 'center' }}>
        <View style={{ width: '100%' }}>
          <MemoCard card={card} tileGradient={moduleGradient.base} />
        </View>
        {!!dots && dots.total > 1 && (
          <View style={{ flexDirection: 'row', columnGap: SPACING['sp-8'] }}>
            {Array.from({ length: dots.total }).map((_unused, i) => (
              <View
                key={`dot-${i}`}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: i === dots.index ? COLORS.content.accent : COLORS.border.secondary,
                }}
              />
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

export const MemoCardFrame = observer(_MemoCardFrame);
